/* =============================================================================
   GDPR-ADAPTER — DSGVO Art. 17/21 Pseudonymisierung (Phase 33, Session 40)
   =============================================================================
   Thin client-side wrapper für den SECURITY-DEFINER-RPC
   `public.anonymize_customer_data(p_email, p_reason)`.

   Verantwortlichkeiten dieses Moduls:
     - Input-Validation client-seitig (Email-Format, Reason-Whitelist, Trimming).
     - Aufruf des RPC über `App.supabase.rpc(...)` mit den geprüften Args.
     - Normalisierung des Result-JSONB in ein stabiles JS-Objekt für die UI.
     - Klare Error-Klassifikation (auth / permission / not_found / unknown),
       sodass die UI sinnvolle Meldungen zeigen kann.

   NICHT-Verantwortlichkeiten:
     - Auth- und Rollen-Check (passiert server-seitig im RPC, owner/admin/manager).
     - Trigger-Bypass / GoBD-Whitelist (passiert in Migration 72 im Trigger).
     - Tatsächliche Pseudonym-Erzeugung (passiert im RPC, sha256-basiert).

   AUFRUFER:
     - Admin-Cockpit-UI (Settings / Datenschutz-Bereich) — folgt in S41+.
     - Manuelle Aufrufe über die Admin-Konsole.

   PATTERN-VERWANDT: src/core/tse-adapter.js (RPC-Wrapper + Cache + Mock-Tests).
   ========================================================================== */

/** @typedef {{
 *    pseudonym: string,
 *    reservations_updated: number,
 *    orders_updated: number,
 *    bons_updated: number,
 *    outbox_purged: number,
 *    audit_id: string
 * }} AnonymizeResult
 */

/** @typedef {'invalid_email'|'unauthenticated'|'forbidden'|'not_found'|'unknown'} GdprErrorKind */

/** @typedef {{ ok: true, data: AnonymizeResult } | { ok: false, kind: GdprErrorKind, message: string }} GdprOutcome */

/* -------------------------------------------------------------------------- */
/* Reason-Whitelist                                                            */
/* -------------------------------------------------------------------------- */

export const GDPR_REASONS = Object.freeze([
  'gdpr_request',         // Art. 17 — Recht auf Löschung
  'gdpr_objection',       // Art. 21 — Widerspruch gegen Verarbeitung
  'admin_cleanup',        // Owner/Admin-Reinigung (z.B. Mitarbeiter-Privatdaten)
  'duplicate_account'     // Doppelter Customer-Account, einer wird anonymisiert
]);

/** Default-Reason, wenn nichts/leeres übergeben. */
export const GDPR_REASON_DEFAULT = 'gdpr_request';

/**
 * Action-String, den die SECURITY-DEFINER-RPC `anonymize_customer_data`
 * server-seitig in `audit_log.action` schreibt. Wird vom Audit-Log-Filter
 * (`audit-log.js`) für die DSGVO-Quick-Pille und vom Settings-Reload-Pfad
 * referenziert. Single-Source statt String-Duplikate (S42, L-09 reduziert).
 */
export const GDPR_AUDIT_ACTION = 'customer.anonymized';

/* -------------------------------------------------------------------------- */
/* Pure-Helpers (testbar)                                                      */
/* -------------------------------------------------------------------------- */

/**
 * Sehr defensive Email-Validierung. Lehnt leer/whitespace/keine @ ab.
 * Keine vollständige RFC-5322-Validierung — der RPC selber prüft nur Leere.
 * Aber wir reichen keine offensichtlich falschen Werte durch.
 */
export function _isValidEmailShape(email) {
  if (typeof email !== 'string') return false;
  const trimmed = email.trim();
  if (trimmed.length === 0) return false;
  if (trimmed.length > 254) return false; // RFC-5321
  if (!trimmed.includes('@')) return false;
  const at = trimmed.indexOf('@');
  if (at === 0 || at === trimmed.length - 1) return false;
  const domain = trimmed.slice(at + 1);
  if (!domain.includes('.')) return false;
  return true;
}

/**
 * Normalisiert den Reason: wenn nicht in Whitelist, fallback auf Default.
 * Niemals fehlerhaft, immer ein gültiger String.
 */
export function _normalizeReason(reason) {
  if (typeof reason !== 'string') return GDPR_REASON_DEFAULT;
  const trimmed = reason.trim();
  if (trimmed.length === 0) return GDPR_REASON_DEFAULT;
  if (GDPR_REASONS.includes(trimmed)) return trimmed;
  return GDPR_REASON_DEFAULT;
}

/**
 * Klassifiziert einen Postgres/Supabase-Error in eine kanonische
 * GdprErrorKind. Robust gegen fehlende Felder.
 */
export function _classifyRpcError(err) {
  if (!err) return 'unknown';
  const code = (err.code || '').toString();
  const msg = (err.message || '').toString();
  if (code === 'invalid_parameter_value' || /Email darf nicht leer/i.test(msg)) {
    return 'invalid_email';
  }
  if (code === 'insufficient_privilege' || /nicht eingeloggt/i.test(msg)) {
    return 'unauthenticated';
  }
  if (/Berechtigung|aktiven Restaurant zugeordnet/i.test(msg)) {
    return 'forbidden';
  }
  if (code === '42883' || /does not exist/i.test(msg)) {
    // RPC nicht deployed, sollte nie passieren in Live
    return 'not_found';
  }
  return 'unknown';
}

/**
 * Normalisiert das RPC-Ergebnis-JSONB in ein stabiles JS-Objekt.
 * Defensiv gegen fehlende Felder (alle counts -> 0, strings -> '').
 */
export function _normalizeResult(raw) {
  const r = raw && typeof raw === 'object' ? raw : {};
  return {
    pseudonym:            typeof r.pseudonym === 'string' ? r.pseudonym : '',
    reservations_updated: Number.isFinite(r.reservations_updated) ? r.reservations_updated : 0,
    orders_updated:       Number.isFinite(r.orders_updated)       ? r.orders_updated       : 0,
    bons_updated:         Number.isFinite(r.bons_updated)         ? r.bons_updated         : 0,
    outbox_purged:        Number.isFinite(r.outbox_purged)        ? r.outbox_purged        : 0,
    audit_id:             typeof r.audit_id === 'string' ? r.audit_id : ''
  };
}

/* -------------------------------------------------------------------------- */
/* Supabase-Resolver                                                            */
/* -------------------------------------------------------------------------- */

function getSupa() {
  const supa = typeof window !== 'undefined' && window.App && window.App.supabase;
  if (!supa) throw new Error('Supabase-Client nicht initialisiert.');
  return supa;
}

/* -------------------------------------------------------------------------- */
/* Public API                                                                  */
/* -------------------------------------------------------------------------- */

/**
 * Anonymisiert einen Customer (Email als Identifier) über den
 * `anonymize_customer_data`-RPC.
 *
 * @param {string} email   Customer-Email — wird trimmed, muss @-Form haben.
 * @param {string} [reason='gdpr_request']  Grund, wird gegen Whitelist geprüft.
 * @returns {Promise<GdprOutcome>}
 *
 * Wirft NICHT — liefert immer ein `GdprOutcome` mit `ok: true|false`.
 */
export async function anonymizeCustomerData(email, reason = GDPR_REASON_DEFAULT) {
  const trimmedEmail = typeof email === 'string' ? email.trim() : '';

  if (!_isValidEmailShape(trimmedEmail)) {
    return {
      ok: false,
      kind: 'invalid_email',
      message: 'Bitte eine gültige Email-Adresse angeben.'
    };
  }

  const safeReason = _normalizeReason(reason);

  let supa;
  try {
    supa = getSupa();
  } catch (e) {
    return {
      ok: false,
      kind: 'unauthenticated',
      message: 'Supabase-Client nicht initialisiert.'
    };
  }

  const { data, error } = await supa.rpc('anonymize_customer_data', {
    p_email: trimmedEmail,
    p_reason: safeReason
  });

  if (error) {
    const kind = _classifyRpcError(error);
    const message = error.message || 'DSGVO-Anonymisierung fehlgeschlagen.';
    return { ok: false, kind, message };
  }

  return { ok: true, data: _normalizeResult(data) };
}

/* -------------------------------------------------------------------------- */
/* S42 — DSGVO-Vorschau                                                        */
/* -------------------------------------------------------------------------- */
/**
 * Vor einer Anonymisierung soll der Operator sehen, was eigentlich betroffen
 * wäre. Memory `feedback_gg_hard_way`: nicht blind ANONYMISIEREN tippen
 * und hoffen, dass die richtige Email getroffen war. Memory
 * `feedback_gg_export_fresh_db`: Counts FRISCH aus DB, nicht aus _state-Cache
 * (Bon-Archive + Mail-Outbox liegen außerhalb des Live-Caches; auch
 * Reservations/Orders sollten konsistent zum Live-Stand sein).
 *
 * Vier parallele head:true / count:'exact'-Queries gegen die vier Tabellen,
 * die die `anonymize_customer_data`-RPC anfasst. RLS sorgt für tenant-
 * Isolation und Rolle-Gating (owner/admin/manager — staff sieht die Tabellen
 * gar nicht und bekäme leere Counts, was operativ auch OK ist).
 */

/**
 * @typedef {{
 *   reservations: number,
 *   orders: number,
 *   bons: number,
 *   outbox: number
 * }} GdprPreviewCounts
 */

/**
 * @typedef {{ ok: true, data: { email: string, counts: GdprPreviewCounts, total: number } }
 *           | { ok: false, kind: GdprErrorKind, message: string }} GdprPreviewOutcome
 */

/** Pure: aggregiert die 4 Einzel-Counts in ein PreviewCounts-Objekt. */
export function _normalizePreviewCounts(raw) {
  const r = raw && typeof raw === 'object' ? raw : {};
  const toNum = (v) => Number.isFinite(v) ? v : 0;
  return {
    reservations: toNum(r.reservations),
    orders:       toNum(r.orders),
    bons:         toNum(r.bons),
    outbox:       toNum(r.outbox)
  };
}

/** Pure: Summe aller Counts. */
export function _previewTotal(counts) {
  const c = _normalizePreviewCounts(counts);
  return c.reservations + c.orders + c.bons + c.outbox;
}

/**
 * Pure: human-readable Summary für die Vorschau-Card.
 * - bei total=0 ein klarer "keine Treffer"-Hinweis (UI kann den Anonymisieren-
 *   Button dann sogar deaktivieren, statt nur einen No-Op-RPC auszuführen).
 */
export function _buildPreviewSummaryText(email, counts) {
  const e = typeof email === 'string' ? email.trim() : '';
  const c = _normalizePreviewCounts(counts);
  const total = _previewTotal(c);
  if (total === 0) {
    return 'Für «' + e + '» sind aktuell keine Datensätze gefunden — '
         + 'die Anonymisierung wäre ein No-Op.';
  }
  return (
    'Für «' + e + '» würden anonymisiert: '
    + c.reservations + ' Reservierungen · '
    + c.orders + ' Bestellungen · '
    + c.bons + ' Bons (HTML-Snapshots bleiben — §147 AO) · '
    + c.outbox + ' Mail-Outbox-Einträge.'
  );
}

/**
 * Liefert Vorschau-Counts FRISCH aus DB für die DSGVO-Anonymisierungs-Aktion.
 *
 * @param {string} email
 * @returns {Promise<GdprPreviewOutcome>}
 *
 * Wirft NICHT — Adapter-Pattern wie anonymizeCustomerData.
 */
export async function previewCustomerData(email) {
  const trimmedEmail = typeof email === 'string' ? email.trim() : '';

  if (!_isValidEmailShape(trimmedEmail)) {
    return {
      ok: false,
      kind: 'invalid_email',
      message: 'Bitte eine gültige Email-Adresse angeben.'
    };
  }

  let supa;
  try {
    supa = getSupa();
  } catch (e) {
    return {
      ok: false,
      kind: 'unauthenticated',
      message: 'Supabase-Client nicht initialisiert.'
    };
  }

  // Vier parallele head-Queries. Bei Erfolg liefert supabase-js
  // { count: <int>, data: null, error: null }. Bei RLS-Block / Auth-Fehler
  // kommt error gesetzt. Wir aggregieren defensive — ein Teil-Fehler
  // (z.B. mail_outbox-Permission) lässt die anderen Counts durch.
  const [resR, resO, resB, resM] = await Promise.all([
    supa.from('reservations').select('id', { head: true, count: 'exact' }).eq('email', trimmedEmail),
    supa.from('orders').select('id', { head: true, count: 'exact' }).eq('customer_email', trimmedEmail),
    supa.from('bon_archive').select('id', { head: true, count: 'exact' }).eq('customer_email', trimmedEmail),
    supa.from('mail_outbox').select('id', { head: true, count: 'exact' }).eq('recipient_email', trimmedEmail)
  ]);

  // Wenn ALLE vier Queries fehlschlagen → echtes Auth-Problem, sonst nur
  // Teile sind null. Ein typischer Verlust: staff-Rolle wirft 401 auf
  // mail_outbox aber sieht reservations; das nehmen wir defensiv an.
  const allFailed = resR.error && resO.error && resB.error && resM.error;
  if (allFailed) {
    const kind = _classifyRpcError(resR.error);
    return {
      ok: false,
      kind,
      message: resR.error.message || 'Vorschau-Abfrage fehlgeschlagen.'
    };
  }

  const counts = _normalizePreviewCounts({
    reservations: resR.error ? 0 : resR.count,
    orders:       resO.error ? 0 : resO.count,
    bons:         resB.error ? 0 : resB.count,
    outbox:       resM.error ? 0 : resM.count
  });

  return {
    ok: true,
    data: {
      email: trimmedEmail,
      counts,
      total: _previewTotal(counts)
    }
  };
}

/* -------------------------------------------------------------------------- */
/* S42 — Customer-Suche per Name / Phone / Email                               */
/* -------------------------------------------------------------------------- */
/**
 * UX-Realität (Jonas-Feedback im S42-Funktionstest): wenn ein DSGVO-Antrag
 * telefonisch reinkommt, hat der Operator oft nur Vor-/Nachnamen oder die
 * Telefonnummer — nicht zwingend die Email. Email-only-Vorschau zwingt dann
 * zur manuellen Recherche in den Listen.
 *
 * Lösung: `searchCustomerProfiles(query)` klassifiziert die Eingabe und sucht
 * je nach Kind in den passenden Spalten von `reservations` + `orders`. Das
 * Ergebnis ist eine Liste von Customer-Profilen, gruppiert nach Email
 * (= kanonischer Identifier für den nachfolgenden Anonymize-RPC).
 *
 * Memory `feedback_gg_hard_way`: nicht mit drei separaten Such-Inputs Yak-
 * Shaving betreiben, sondern eine intelligente Auto-Klassifikation.
 */

/** @typedef {{ email: string, name: string, phone: string, reservationCount: number,
 *              orderCount: number, lastActivityAt: string|null }} CustomerProfile
 */
/** @typedef {'email'|'phone'|'name'|'invalid'} QueryKind */

/**
 * Pure: erkennt automatisch, ob die Eingabe wie Email, Telefon oder Name aussieht.
 * Reihenfolge:
 *   1. @-Zeichen → 'email' (wenn _isValidEmailShape sagt OK; sonst 'invalid')
 *   2. überwiegend Digits + (+, /, -, Leerzeichen) → 'phone'
 *   3. sonst → 'name'
 *   4. leerer Trim → 'invalid'
 */
export function _classifyQueryInput(query) {
  if (typeof query !== 'string') return 'invalid';
  const trimmed = query.trim();
  if (trimmed.length === 0) return 'invalid';

  if (trimmed.includes('@')) {
    return _isValidEmailShape(trimmed) ? 'email' : 'invalid';
  }

  // Phone: ≥3 Zeichen, ≥60% Digits in den nicht-trennzeichen-Zeichen.
  const digits = trimmed.replace(/[^0-9]/g, '');
  const nonSpace = trimmed.replace(/[\s\-\/().+]/g, '');
  if (digits.length >= 3 && nonSpace.length > 0 && (digits.length / nonSpace.length) >= 0.6) {
    return 'phone';
  }

  // Name: min 2 Zeichen, nicht nur Sonderzeichen.
  if (trimmed.replace(/[^a-zA-ZäöüÄÖÜß]/g, '').length >= 2) {
    return 'name';
  }
  return 'invalid';
}

/**
 * Pure: aggregiert Reservierungs- + Order-Rows zu einer Profile-Map per Email.
 * Rows ohne Email werden ignoriert (kein anonymizable Profile-Anker).
 * Pro Email wird je Bereich gezählt; Name / Phone werden mit dem zuerst
 * gesehenen nicht-leeren Wert befüllt (älteste Quelle, deterministisch über
 * Sort-Reihenfolge).
 */
export function _normalizeCustomerProfiles(reservationRows, orderRows) {
  const map = new Map();

  function touch(email) {
    const key = (email || '').trim().toLowerCase();
    if (!key) return null;
    if (!map.has(key)) {
      map.set(key, {
        email: key,
        name: '',
        phone: '',
        reservationCount: 0,
        orderCount: 0,
        lastActivityAt: null
      });
    }
    return map.get(key);
  }

  function bumpActivity(profile, iso) {
    if (!iso) return;
    if (!profile.lastActivityAt || iso > profile.lastActivityAt) {
      profile.lastActivityAt = iso;
    }
  }

  for (const row of Array.isArray(reservationRows) ? reservationRows : []) {
    if (!row || typeof row !== 'object') continue;
    const p = touch(row.email);
    if (!p) continue;
    p.reservationCount++;
    if (!p.name && row.name)  p.name = row.name;
    if (!p.phone && row.phone) p.phone = row.phone;
    bumpActivity(p, row.created_at || row.date || null);
  }

  for (const row of Array.isArray(orderRows) ? orderRows : []) {
    if (!row || typeof row !== 'object') continue;
    const p = touch(row.customer_email);
    if (!p) continue;
    p.orderCount++;
    if (!p.name && row.customer_name)   p.name = row.customer_name;
    if (!p.phone && row.customer_phone) p.phone = row.customer_phone;
    bumpActivity(p, row.created_at || null);
  }

  return Array.from(map.values());
}

/** Pure: sortiert Profile nach lastActivityAt absteigend (neueste zuerst). */
export function _sortProfilesByLastActivity(profiles) {
  const list = Array.isArray(profiles) ? profiles.slice() : [];
  list.sort((a, b) => {
    const aa = a && a.lastActivityAt ? a.lastActivityAt : '';
    const bb = b && b.lastActivityAt ? b.lastActivityAt : '';
    if (aa === bb) return 0;
    return bb.localeCompare(aa);
  });
  return list;
}

/**
 * @typedef {{ ok: true, data: { kind: QueryKind, profiles: CustomerProfile[], query: string } }
 *           | { ok: false, kind: GdprErrorKind, message: string }} CustomerSearchOutcome
 */

/**
 * Sucht Customer-Profile aus `reservations` + `orders` per
 * Name/Phone/Email-Fragment. Kind wird via `_classifyQueryInput` ermittelt.
 *
 * @param {string} query
 * @returns {Promise<CustomerSearchOutcome>}
 *
 * Wirft NIE. Memory `feedback_gg_export_fresh_db`: Suche direkt aus DB,
 * nicht aus _state.reservations/orders-Cache (das wäre 30d-cutoff-begrenzt).
 */
export async function searchCustomerProfiles(query) {
  const trimmed = typeof query === 'string' ? query.trim() : '';
  const kind = _classifyQueryInput(trimmed);

  if (kind === 'invalid') {
    return {
      ok: false,
      kind: 'invalid_email',
      message: 'Bitte Email, Name oder Telefonnummer eingeben (mind. 2 Buchstaben oder 3 Ziffern).'
    };
  }

  // Email-Kind delegiert an die spezialisierte Vorschau-Funktion über den
  // UI-Handler — hier nicht doppeln. Wir geben dem Caller zurück, dass es
  // sich um eine Email handelt, damit er den richtigen Pfad wählt.
  if (kind === 'email') {
    return {
      ok: true,
      data: {
        kind,
        query: trimmed,
        profiles: [{ email: trimmed, name: '', phone: '', reservationCount: 0, orderCount: 0, lastActivityAt: null }]
      }
    };
  }

  let supa;
  try {
    supa = getSupa();
  } catch (e) {
    return {
      ok: false,
      kind: 'unauthenticated',
      message: 'Supabase-Client nicht initialisiert.'
    };
  }

  // PostgREST: `ilike.*<pattern>*` mit Wildcard `*` (= SQL `%`).
  const pattern = '*' + trimmed.replace(/[*%]/g, '') + '*';
  const limit = 100;

  let resRows = null, orderRows = null;
  let resErr = null, orderErr = null;

  if (kind === 'name') {
    const r = await supa
      .from('reservations')
      .select('email, name, phone, created_at, date')
      .ilike('name', pattern)
      .not('email', 'is', null)
      .limit(limit);
    resRows = r.data; resErr = r.error;

    const o = await supa
      .from('orders')
      .select('customer_email, customer_name, customer_phone, created_at')
      .ilike('customer_name', pattern)
      .not('customer_email', 'is', null)
      .limit(limit);
    orderRows = o.data; orderErr = o.error;
  } else if (kind === 'phone') {
    const r = await supa
      .from('reservations')
      .select('email, name, phone, created_at, date')
      .ilike('phone', pattern)
      .not('email', 'is', null)
      .limit(limit);
    resRows = r.data; resErr = r.error;

    const o = await supa
      .from('orders')
      .select('customer_email, customer_name, customer_phone, created_at')
      .ilike('customer_phone', pattern)
      .not('customer_email', 'is', null)
      .limit(limit);
    orderRows = o.data; orderErr = o.error;
  }

  if (resErr && orderErr) {
    return {
      ok: false,
      kind: _classifyRpcError(resErr),
      message: resErr.message || 'Suche fehlgeschlagen.'
    };
  }

  const profiles = _sortProfilesByLastActivity(
    _normalizeCustomerProfiles(resRows, orderRows)
  );

  return {
    ok: true,
    data: { kind, query: trimmed, profiles }
  };
}
